// src/components/Eventos/EventsHeats/components/EventHeatRow.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TableRow, TableCell, Box, Typography, Avatar, Stack, Chip, 
  IconButton, Collapse, Grid, Tooltip, useTheme, useMediaQuery
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonIcon from '@mui/icons-material/Person';
import { formatWithPrecision } from '../../../../utils/precision';
import { useEventContext, fieldHorizontalEvents, fieldVerticalEvents } from '../../../../context/EventContext';
import { getPlaceColors, isSpecial, getFontSize } from '../utils/eventHelpers';
import { getSplitDistance } from '../utils/trackDistances';
import LapSplitCard from './LapSplitCard';
import FieldAttemptCard from './FieldAttemptCard';
import VerticalAttemptCard from './VerticalAttemptCard';

const EventHeatRow = React.memo(({ 
  place, line, number, name, team, result,
  isTrackEvent, enableWind, wind, reactionTime,
  attemptsData, bestAttemptIndex, lapTimes,
  timeRaw, athPrec, passed, hist, totalLaps,
  chips, score, year, athletePhoto, teamLogo,
  showHeat, heatNum, hasPlaceOrResult,
  // Nuevas props para tracking de posiciones
  fieldPositions, attemptsWithPositions, maxAttempts,
  // Nueva prop para el código del evento
  eventCode = null,
  // Nuevas props para eventos verticales
  heightsData = null, // Datos de alturas para eventos verticales
  verticalAttempts = null // Intentos por altura para eventos verticales
}) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const { competitionId, precision: globalPrecision } = useEventContext();
  
  // Responsive breakpoints
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.down('md'));
  const isMd = useMediaQuery(theme.breakpoints.down('lg'));
  
  // Determinar tipo de evento
  const isVerticalEvent = eventCode && fieldVerticalEvents.includes(eventCode);
  const isHorizontalEvent = eventCode && fieldHorizontalEvents.includes(eventCode);
  
  const hasAttempts = !isTrackEvent && !isVerticalEvent && attemptsWithPositions && attemptsWithPositions.length > 0;
  const hasVerticalAttempts = isVerticalEvent && verticalAttempts && verticalAttempts.length > 0 && 
    verticalAttempts.some(height => 
      height.attempts && 
      height.attempts.some(attempt => 
        attempt && 
        typeof attempt === 'string' && 
        attempt.trim() !== '' && 
        attempt !== '-'
      )
    );
  const hasLap = lapTimes?.length > 0;
  const showRaw = typeof timeRaw === 'number' && athPrec !== globalPrecision;
  const hasExpandableContent = hasAttempts || hasVerticalAttempts || hasLap;
  
  const toggleExpand = e => {
    e.stopPropagation();
    setOpen(o => !o);
  };

  const goAth = id => {
    console.log('🔍 goAth - Navegando a atleta:', id);
    console.log('🔍 goAth - URL completa:', `/${competitionId}/athlete/${id}`);
    navigate(`/${competitionId}/athlete/${id}`);
  };
  
  const goTeam = teamName => {
    console.log('🔍 goTeam - Navegando a equipo:', teamName);
    console.log('🔍 goTeam - URL encoded:', encodeURIComponent(teamName));
    console.log('🔍 goTeam - URL completa:', `/${competitionId}/team/${encodeURIComponent(teamName)}`);
    navigate(`/${competitionId}/team/${encodeURIComponent(teamName)}`);
  };
  

  // Función para determinar el color de fila
  const getRowBackground = () => {
    if (!place || place === '-' || isNaN(place)) return 'transparent';
    const pos = +place;
    return pos % 2 === 0 
      ? 'rgba(0, 0, 0, 0.02)' // Filas pares - gris muy claro
      : 'rgba(25, 118, 210, 0.02)'; // Filas impares - azul muy claro
  };

  const renderChip = (ch) => {
    let chipProps = {};
    
    switch(ch) {
      case 'PB':
        chipProps = {
          color: 'info',
          variant: 'filled',
          icon: <span style={{fontSize: '14px', fontWeight: 'bold'}}>★</span>,
          sx: {
            background: '#2196f3',
            color: 'white',
            fontWeight: 'bold',
            borderRadius: '4px',
            boxShadow: '0 2px 2px 0 rgba(33, 150, 243, 0.2)',
          }
        };
        break;
      case 'SB':
        chipProps = {
          color: 'info',
          variant: 'filled',
          icon: <span style={{fontSize: '14px'}}>⏱</span>,
          sx: {
            background: '#4dabf5',
            color: 'white',
            fontWeight: 'bold',
            borderRadius: '4px',
            boxShadow: '0 2px 2px 0 rgba(33, 150, 243, 0.2)',
          }
        };
        break;
      case 'MR':
        chipProps = {
          color: 'warning',
          variant: 'filled',
          icon: <span style={{fontSize: '14px'}}>🏆</span>,
          sx: {
            background: '#ff9800',
            color: 'white',
            fontWeight: 'bold',
            borderRadius: '4px',
            boxShadow: '0 2px 2px 0 rgba(255, 152, 0, 0.2)',
          }
        };
        break;
      case 'Q':
        chipProps = {
          color: 'success',
          variant: 'filled',
          sx: {
            background: '#4caf50',
            color: 'white',
            fontWeight: 'bold',
            borderRadius: '4px',
            boxShadow: '0 2px 2px 0 rgba(76, 175, 80, 0.2)',
          }
        };
        break;
      case 'q':
        chipProps = {
          color: 'success',
          variant: 'outlined',
          sx: {
            color: '#4caf50',
            fontWeight: 'bold',
            borderRadius: '4px',
            border: '2px solid #4caf50',
          }
        };
        break;
      case 'qR':
      case 'qJ':
      case 'qD':
        chipProps = {
          color: 'success',
          variant: 'outlined',
          sx: {
            color: '#4caf50',
            fontWeight: 'bold',
            borderRadius: '4px',
            border: '2px dashed #4caf50',
          }
        };
        break;
      default:
        chipProps = {
          color: 'success',
          variant: 'outlined',
          sx: {
            borderRadius: '4px',
          }
        };
    }
    
    return (
      <Chip 
        key={ch} 
        label={ch}
        size="small"
        {...chipProps}
        sx={{
          ...chipProps.sx,
          fontSize: getFontSize(11, 1, isXs, isSm, isMd),
          height: isXs ? 20 : 24,
          mb: 0.5,
          ml: 0.5,
          mr: 0.5
        }}
      />
    );
  };

  return (
    <>
      <TableRow
        sx={[
          {
            transition: 'background-color 0.2s ease-in-out',
            backgroundColor: getRowBackground(),
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            }
          },
          hasExpandableContent ? { cursor: 'pointer' } : {}
        ]}
        onClick={hasExpandableContent ? toggleExpand : undefined}
      >
        {hasPlaceOrResult && (
          <TableCell 
            align="center"
            sx={{
              p: 0,
              position: 'relative',
              minWidth: isXs ? 70 : 90,
              maxWidth: isXs ? 70 : 90,
              height: isXs ? 70 : 80,
              border: 'none',
            }}
          >
            {place && place !== '-' && !isNaN(place) ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  minHeight: isXs ? 70 : 80,
                  backgroundColor: getPlaceColors(place).bg,
                  color: 'white',
                  fontWeight: '900',
                  fontSize: getFontSize(+place <= 3 ? 40 : 36, 1, isXs, isSm, isMd),
                  textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                  borderRadius: 0,
                  transition: 'all 0.2s ease-in-out',
                  fontFamily: '"Roboto", "Arial", sans-serif',
                  letterSpacing: '1px',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    backgroundColor: getPlaceColors(place).border,
                  }
                }}
              >
                {place}
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  minHeight: isXs ? 70 : 80,
                  backgroundColor: '#f5f5f5',
                  color: theme.palette.text.secondary,
                  fontSize: getFontSize(18, 1, isXs, isSm, isMd),
                  fontWeight: 'medium',
                  border: '1px solid #e0e0e0'
                }}
              >
                {place || '-'}
              </Box>
            )}
          </TableCell>
        )}

        {!hasPlaceOrResult && <TableCell align="center">{line}</TableCell>}
        {!hasPlaceOrResult && <TableCell align="center">{number}</TableCell>}

        {year && (
          <TableCell 
            align="center"
            sx={{
              fontSize: getFontSize(14, 1, isXs, isSm, isMd),
              p: isXs ? 0.75 : 1.5,
              fontWeight: 'medium'
            }}
          >
            <Chip 
              label={year}
              size="small"
              variant="outlined"
              sx={{
                fontSize: getFontSize(12, 1, isXs, isSm, isMd),
                fontWeight: 'medium',
                backgroundColor: '#f8f9fa',
                borderColor: theme.palette.primary.main
              }}
            />
          </TableCell>
        )}

        <TableCell
          sx={{
            minWidth: isXs ? '200px' : '250px',
            maxWidth: isXs ? '200px' : '300px'
          }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}>
            <Avatar
              src={athletePhoto}
              alt={name}
              sx={{
                width: isXs ? 32 : 40,
                height: isXs ? 32 : 40,
                bgcolor: theme.palette.primary.light,
                fontSize: isXs ? 14 : 16,
                boxShadow: theme.shadows[2],
                border: '2px solid white'
              }}
            >
              {!athletePhoto && name ? name.charAt(0).toUpperCase() : <PersonIcon fontSize="small" />}
            </Avatar>
            <Box>
              <Typography 
                sx={{
                  fontSize: getFontSize(16, 1, isXs, isSm, isMd),
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  textDecoration: 'none',
                  borderBottom: `2px dotted ${theme.palette.primary.main}`,
                  cursor: 'pointer',
                  display: 'inline-block',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    color: theme.palette.primary.main,
                    borderBottomStyle: 'solid'
                  }
                }}
                onClick={e => { e.stopPropagation(); goAth(number); }}
              >
                {name}
              </Typography>
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                mt: 0.5
              }}>
                <Avatar
                  src={teamLogo}
                  alt={team}
                  sx={{
                    width: isXs ? 18 : 22,
                    height: isXs ? 18 : 22,
                    mr: 0.5,
                    bgcolor: theme.palette.secondary.light,
                    fontSize: isXs ? 8 : 10,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      bgcolor: theme.palette.secondary.main
                    }
                  }}
                  onClick={e => { e.stopPropagation(); goTeam(team); }}
                >
                  {!teamLogo && team ? team.charAt(0).toUpperCase() : ''}
                </Avatar>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{
                    fontSize: getFontSize(14, 1, isXs, isSm, isMd),
                    maxWidth: isXs ? '140px' : '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontWeight: 'medium',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    borderBottom: `1px dotted ${theme.palette.secondary.main}`,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      color: theme.palette.secondary.main,
                      borderBottomStyle: 'solid',
                      transform: 'translateX(2px)'
                    }
                  }}
                  onClick={e => { e.stopPropagation(); goTeam(team); }}
                >
                  {team}
                </Typography>
              </Box>
              {hasPlaceOrResult && (
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{
                    fontSize: getFontSize(12, 1, isXs, isSm, isMd),
                    display: 'block',
                    mt: 0.25,
                    opacity: 0.8
                  }}
                >
                  #{number} {line && `| Línea: ${line}`}
                </Typography>
              )}
            </Box>
          </Box>
        </TableCell>

        {hasPlaceOrResult && (
          <>
            <TableCell 
              align="center" 
              sx={{
                verticalAlign: 'top',
                color: isSpecial(result) 
                  ? theme.palette.error.main 
                  : theme.palette.text.primary,
                p: isXs ? 0.75 : 1.5,
              }}
            >
              {isSpecial(result)
                ? <Typography sx={{
                    fontSize: getFontSize(16, 1, isXs, isSm, isMd),
                    fontWeight: 'medium'
                  }}>
                    {result.toUpperCase()}
                  </Typography>
                : <>
                    <Stack 
                      direction="row" 
                      spacing={0.5} 
                      justifyContent="center"
                      flexWrap="wrap"
                      sx={{ mb: isXs ? 0.5 : 1 }}
                    >
                      {chips.map(renderChip)}
                    </Stack>
                    <Typography 
                      sx={{
                        fontSize: getFontSize(22, 1, isXs, isSm, isMd),
                        fontWeight: 'bold',
                      }}
                    >
                      {!isTrackEvent && result && result !== '-' && !isSpecial(result)
                        ? result
                        : typeof timeRaw === 'number'
                          ? formatWithPrecision(timeRaw, globalPrecision)
                          : result !== '-' ? result : '-'}
                    </Typography>
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexWrap: 'wrap'
                    }}>
                      {showRaw && (
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{
                            fontSize: getFontSize(12, 1, isXs, isSm, isMd)
                          }}
                        >
                          ({formatWithPrecision(timeRaw, athPrec)})
                        </Typography>
                      )}
                      {isTrackEvent && enableWind && wind?.trim() && (
                        <Tooltip title="Viento">
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{
                              ml: 1,
                              fontSize: getFontSize(12, 1, isXs, isSm, isMd),
                              bgcolor: theme.palette.background.default,
                              px: 0.5,
                              borderRadius: 0.5
                            }}
                          >
                            {wind}
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                    {reactionTime != null && (
                      <Tooltip title="Tiempo de reacción">
                        <Typography 
                          variant="caption" 
                          sx={{
                            fontSize: getFontSize(12, 1, isXs, isSm, isMd),
                            display: 'inline-block',
                            bgcolor: '#f9a825',
                            color: '#fff',
                            px: 0.5,
                            py: 0.2,
                            borderRadius: 0.5,
                            mt: 0.5,
                            fontWeight: 'medium',
                            boxShadow: '0 1px 2px 0 rgba(0,0,0,0.1)'
                          }}
                        >
                          RT: {reactionTime}
                        </Typography>
                      </Tooltip>
                    )}
                  </>
              }
              {hasExpandableContent && (
                <IconButton 
                  size="small" 
                  onClick={toggleExpand}
                  sx={{ 
                    mt: 0.5,
                    opacity: 0.7,
                    '&:hover': { opacity: 1 }
                  }}
                >
                  {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
              )}
            </TableCell>

            {showHeat && (
              <TableCell 
                align="center"
                sx={{
                  fontSize: getFontSize(16, 1, isXs, isSm, isMd),
                  p: isXs ? 0.75 : 1.5
                }}
              >
                <Chip 
                  label={heatNum} 
                  variant="outlined"
                  size="small"
                  sx={{
                    fontSize: getFontSize(14, 1, isXs, isSm, isMd),
                    fontWeight: 'medium',
                    minWidth: 32,
                    backgroundColor: '#f8f9fa',
                    borderColor: theme.palette.primary.main
                  }}
                />
              </TableCell>
            )}

            {score != null && (
              <TableCell 
                align="center"
                sx={{
                  fontSize: getFontSize(16, 1, isXs, isSm, isMd),
                  fontWeight: 'medium',
                  p: isXs ? 0.75 : 1.5
                }}
              >
                <Chip 
                  label={score}
                  variant="filled"
                  size="small"
                  sx={{
                    fontSize: getFontSize(14, 1, isXs, isSm, isMd),
                    fontWeight: 'bold',
                    backgroundColor: theme.palette.success.main,
                    color: 'white'
                  }}
                />
              </TableCell>
            )}
          </>
        )}
      </TableRow>

      {hasExpandableContent && (
        <TableRow>
          <TableCell
            colSpan={
              hasPlaceOrResult
                ? (score != null
                  ? (showHeat ? 7 : 6)
                  : (showHeat ? 6 : 5))
                : (showHeat ? 6 : 5)
            }
            sx={{ 
              p: 0,
              borderBottom: 0,
              backgroundColor: theme.palette.action.hover
            }}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ 
                px: isXs ? 1 : 2,
                py: isXs ? 1 : 1.5,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}>
                {/* Eventos Horizontales */}
                {hasAttempts && !isVerticalEvent && (
                  <Box sx={{ mb: 2 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        mb: 2, 
                        fontSize: getFontSize(16, 1, isXs, isSm, isMd),
                        fontWeight: 'bold',
                        color: theme.palette.primary.main
                      }}
                    >
                      Intentos en Campo
                    </Typography>
                    <Grid container spacing={1.5}>
                      {attemptsWithPositions.map((attempt, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <FieldAttemptCard
                            attemptNumber={attempt.attemptNumber}
                            result={attempt.result}
                            position={attempt.position}
                            positionChange={attempt.positionChange}
                            wind={attempt.wind}
                            isBestAttempt={bestAttemptIndex === index}
                            isValidAttempt={attempt.isValidAttempt}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Eventos Verticales */}
                {hasVerticalAttempts && isVerticalEvent && (
                  <Box sx={{ mb: 2 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        mb: 2, 
                        fontSize: getFontSize(16, 1, isXs, isSm, isMd),
                        fontWeight: 'bold',
                        color: theme.palette.primary.main
                      }}
                    >
                      Intentos por Altura
                    </Typography>
                    <Grid container spacing={1.5}>
                      {verticalAttempts.map((heightData, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <VerticalAttemptCard
                            height={heightData.height}
                            attempts={heightData.attempts}
                            position={heightData.position}
                            positionChange={heightData.positionChange}
                            isCurrentHeight={heightData.isCurrentHeight}
                            isBestHeight={heightData.isBestHeight}
                            isActive={heightData.isActive || false}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Eventos de Pista - Splits */}
                {hasLap && (
                  <Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        mb: 2, 
                        fontSize: getFontSize(16, 1, isXs, isSm, isMd),
                        fontWeight: 'bold',
                        color: theme.palette.primary.main
                      }}
                    >
                      Splits por Vuelta
                    </Typography>
                    <Grid container spacing={1.5}>
                      {[...Array(totalLaps)].map((_, i) => (
                        <Grid item xs={12} sm={6} md={4} key={i}>
                          <LapSplitCard
                            lapNumber={i + 1}
                            splitTime={hist.lap?.[i]}
                            cumulativeTime={hist.cumul?.[i]}
                            position={hist.pos?.[i]}
                            positionChange={hist.chg?.[i]}
                            isActive={i === totalLaps - 1}
                            distance={eventCode ? getSplitDistance(eventCode, i + 1) : null}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
});

EventHeatRow.displayName = 'EventHeatRow';

export default EventHeatRow;